import { 
  countPrecedingLineElements, 
  collectTextNodes, 
  splitNodesIntoPages,
  splitTextElementsAcrossPages,
  replaceAllTextElementsWithPages,
  replaceTextElementWithPages
} from './utils';
import { LayoutType } from '../../ExtendedContent/ExtendedContentContainer/layout';
import { normalizeAnswerSections } from 'util/topicStructure';

// 量測誤差的安全邊界：量測容器沒有 .abreast 祖先，拿不到 flex gap／捲軸等零星佔位，
// 留 2% 避免每頁最後一行剛好被擠出可視範圍。
const IMAGE_PAGE_SAFETY_RATIO = 0.98;

/**
 * 圖片型：算出每一頁右欄文字可用的像素高度。
 *
 * 版面為 .imageSet(column flex) > .title + .abreast(剩餘高度) > .img + .text，
 * 標題列每頁都在，所以每頁可用高度一律是「容器高度 - 標題列（含上下 margin）」。
 *
 * 取不到容器高度時（例如小視窗尚未完成佈局）退回舊的行數換算，確保不會回傳 0。
 */
const measureImagePageHeight = ({ measureContainer, titleElements, fullWidthCss, fallbackHeight }) => {
  const containerHeight = measureContainer._containerHeight || 0;
  if (containerHeight <= 0) return fallbackHeight;

  let titleBoxHeight = 0;
  if (titleElements.length > 0) {
    // 標題列是滿版寬（不像右欄文字只有一半），量測時要用容器原始寬度
    const widthBeforeMeasure = measureContainer.style.width;
    measureContainer.style.width = fullWidthCss;

    titleElements.forEach((titleElement) => {
      if (measureContainer.setHTMLAndApplyBrStyles) {
        measureContainer.setHTMLAndApplyBrStyles(titleElement.outerHTML);
      } else {
        measureContainer.innerHTML = titleElement.outerHTML;
      }
      const target = measureContainer._innerContainer || measureContainer;
      const titleEl = target.querySelector('.title');
      if (!titleEl) return;

      void titleEl.offsetHeight; // 強制重排
      const titleStyle = window.getComputedStyle(titleEl);
      // offsetHeight 含 padding 但不含 margin，.imageSet .title 有 margin:1% 0，要另外加回來
      titleBoxHeight +=
        titleEl.offsetHeight +
        (parseFloat(titleStyle.marginTop) || 0) +
        (parseFloat(titleStyle.marginBottom) || 0);
    });

    measureContainer.style.width = widthBeforeMeasure;
  }

  const available = (containerHeight - titleBoxHeight) * IMAGE_PAGE_SAFETY_RATIO;
  return available > 0 ? available : fallbackHeight;
};

/**
 * 處理 .text 結構的分頁邏輯（原有邏輯）
 */
export const handleTextStructure = (doc, textElements, lineHeight, pageSize, measureContainer, extendedResourceStyle) => {
  let actualTotalPages = 0;

  // 先把巢狀在 #question 內的 #answer / #analyze 搬成兄弟節點，讓切法只由內容決定、
  // 不由內容檔漏寫 </div> 決定（見 util/topicStructure）。
  // 不影響 textElements：#answer / #analyze 內沒有 .text。
  normalizeAnswerSections(doc);

  // === 新增：偵測多區塊結構，若有則跳過預檢查 ===
  const hasMultiSectionPreCheck = !!(
    doc.querySelector('#question') && 
    (doc.querySelector('#answer') || doc.querySelector('#analyze'))
  );
  
  // ===== 新增：計算總行數，如果 <= 8 行就不分頁 =====
  // 多區塊結構（#question + #answer/#analyze）必須跳過此預檢查：
  // 答案、詳解/解析依規格「一律獨立到下一頁」，若因總行數 <= 8 而不分頁，
  // 會讓題目與答案、解析全部擠在同一頁（NBK-1438）。
  if (textElements.length > 0 && extendedResourceStyle !== LayoutType.Image_108_2 && !hasMultiSectionPreCheck) {
    const firstTextElement = textElements[0];
    // 1. 計算前置元素（標題圖片、<br>）占用的行數
    let precedingLines = 0;
    const allBrElements = Array.from(doc.querySelectorAll('br'));
    const allImages = doc.querySelectorAll('img');
    const allImgDivs = doc.querySelectorAll('div.img');
    const firstImgDiv = allImgDivs.length > 0 ? allImgDivs[0] : null;
    
    // 計算在第一個 .text 之前的 <br> 和 <img> 數量
    allBrElements.forEach(br => {
      if (br.compareDocumentPosition(firstTextElement) & Node.DOCUMENT_POSITION_FOLLOWING) {
        precedingLines += 1;
      }
    });
    
    allImages.forEach(img => {
      const isInFirstImgDiv = firstImgDiv && firstImgDiv.contains(img);
      const isPreceding = img.compareDocumentPosition(firstTextElement) & Node.DOCUMENT_POSITION_FOLLOWING;

      if (isInFirstImgDiv || isPreceding) {
        precedingLines += 1; // 每個標題圖片算1行
      }
    });
    
    // 2. 測量所有 .text 元素的總高度
    let totalContentHeight = 0;
    const imgMaxH = measureContainer._containerHeight || (pageSize * lineHeight);
    
    textElements.forEach((textElement) => {
      const tempDiv = document.createElement('div');
      tempDiv.className = 'text';
      tempDiv.innerHTML = textElement.innerHTML;
      measureContainer.innerHTML = tempDiv.outerHTML;
      
      // 確保圖片最大高度與實際顯示容器一致
      measureContainer.querySelectorAll('img').forEach(img => {
        img.style.maxHeight = `${imgMaxH}px`;
        img.style.maxWidth = '100%';
        img.style.objectFit = 'contain';
      });
      void measureContainer.offsetHeight; // 強制重排
      
      totalContentHeight += measureContainer.offsetHeight;
    });
    
    // 2.5. 測量所有 .title 元素的總高度（新增）
    const titleElements = doc.querySelectorAll('.title');
    titleElements.forEach((titleElement) => {
      const tempDiv = document.createElement('div');
      tempDiv.className = 'title';
      tempDiv.innerHTML = titleElement.innerHTML;
      measureContainer.innerHTML = tempDiv.outerHTML;
      totalContentHeight += measureContainer.offsetHeight;
    });
    
    // 3. 計算總行數
    const contentLines = Math.ceil(totalContentHeight / lineHeight);
    const totalLines = precedingLines + contentLines;

    // console.log('=== 預檢查階段：總行數計算 ===');
    // console.log('totalContentHeight (像素)', totalContentHeight);
    // console.log('lineHeight (像素)', lineHeight);
    // console.log('contentLines (行數)', contentLines);
    // console.log('precedingLines (行數)', precedingLines);
    // console.log('totalLines (總行數)', totalLines);
    
    // 4. 如果總行數 <= 8，不分頁，直接返回
    if (totalLines <= pageSize) {
      const allTextElements = doc.querySelectorAll('.text');
      return {
        htmlString: doc.body.innerHTML,
        totalPages: allTextElements.length || 1,
      };
    }

    // ★ 4.5 像素級保險：行數超過但實際像素高度仍在容器內，也不分頁
    //   解決圖片高度換算行數不合理的問題
    if (measureContainer._containerHeight > 0) {
      const totalPixelHeight = totalContentHeight + (precedingLines * lineHeight);
      if (totalPixelHeight <= measureContainer._containerHeight) {
        const allTextElements = doc.querySelectorAll('.text');
        return {
          htmlString: doc.body.innerHTML,
          totalPages: allTextElements.length || 1,
        };
      }
    }
  }

  // 處理第一個 .text 元素，識別標題圖片和其他圖片
  // 先找到第一個 <div class="img">，讓它在整個函數中都可訪問
  const allImgDivs = doc.querySelectorAll('div.img');
  const firstImgDiv = allImgDivs.length > 0 ? allImgDivs[0] : null;
  
  if (textElements.length > 0) {
    const firstTextElement = textElements[0];
    
    // 找到所有圖片
    const allImages = doc.querySelectorAll('img');
    const titleImages = []; // 標題圖片（在第一個 div.img 內的）
    const otherImages = []; // 其他圖片（不在第一個 div.img 內的）
    
    // 題目型（topic）：#question 內的附圖（例如 .subject 的數線圖）是題目本體的一部分，
    // 不可以被抽成「獨立一頁」。因為 #question 本身不會被分頁隱藏（display.js 只 toggle
    // .text），把圖片綁到某一頁之後，題幹所在的頁面反而看不到圖（NBK-1662 圖片消失）。
    const isTopicLayout = extendedResourceStyle === LayoutType.Topic_108_2;
    const isQuestionContentImage = (img) =>
      isTopicLayout &&
      !!img.closest('#question') &&
      !img.closest('.img') &&
      !img.closest('.imgSize');

    allImages.forEach(img => {
      // 檢查圖片是否在第一個 div.img 內
      if (firstImgDiv && firstImgDiv.contains(img)) {
        titleImages.push(img);
      } else if (isQuestionContentImage(img)) {
        // 跟著題目留在流程裡，不標記獨立頁、不隱藏
        titleImages.push(img);
      } else {
        // 檢查圖片是否在第一個 .text 之前（可能是標題圖片但不在 div.img 內）
        const isPreceding = img.compareDocumentPosition(firstTextElement) & Node.DOCUMENT_POSITION_FOLLOWING;
        if (isPreceding) {
          // 在第一個 .text 之前但不是標題圖片的，也視為標題圖片（固定在第一頁）
          titleImages.push(img);
        } else {
          // 其他圖片，需要獨立一頁顯示
          otherImages.push(img);
        }
      }
    });
    
    // 標記非標題圖片，後續會為它們創建獨立分頁
    otherImages.forEach((img, index) => {
      // 將圖片標記為獨立頁面顯示
      img.setAttribute('data-standalone-page', 'true');
      // 暫時隱藏這些圖片，避免影響文字高度測量
      img.style.display = 'none'; 
      // 注意：之後在 display.js 中顯示時，需要把 display 改回來（變成 block 或 inline）
    });
  }
  
  // ===== 計算 .title 元素的實際高度（像素）和行數，用於第一頁扣除 =====
  let titleHeight = 0;
  let titleLines = 0;
  const titleElements = doc.querySelectorAll('.title');
  
  if (titleElements.length > 0) {
    // 方法：直接測量每個 .title 元素的實際高度
    // 建立一個臨時的測量容器，確保樣式正確繼承
    const tempMeasureContainer = document.createElement('div');
    tempMeasureContainer.style.position = 'absolute';
    tempMeasureContainer.style.visibility = 'hidden';
    tempMeasureContainer.style.width = measureContainer.style.width || '100%';
    tempMeasureContainer.className = measureContainer.className + ' plain_text';
    document.body.appendChild(tempMeasureContainer);
    
    // 確保 <br> 標籤不佔用高度的函數
    const applyBrStyles = (container) => {
      const brElements = container.querySelectorAll('br');
      brElements.forEach(br => {
        br.style.display = 'none';
      });
    };
    
    try {
      titleElements.forEach((titleElement) => {
        const tempDiv = document.createElement('div');
        tempDiv.className = 'title';
        tempDiv.innerHTML = titleElement.innerHTML;
        tempMeasureContainer.innerHTML = '';
        tempMeasureContainer.appendChild(tempDiv);
        
        // 重要：隱藏 <br> 標籤，因為實際樣式中 br { display: none; }
        applyBrStyles(tempMeasureContainer);
        
        // 測量單個 .title 元素的高度（此時 <br> 已被隱藏）
        const singleTitleHeight = tempDiv.offsetHeight;
        titleHeight += singleTitleHeight;
        
        // console.log('單個 .title 元素高度（已隱藏 <br>）:', singleTitleHeight, '像素');
      });
      
      titleLines = Math.ceil(titleHeight / lineHeight);
      
      // console.log('=== .title 元素測量 ===');
      // console.log('titleElements 數量', titleElements.length);
      // console.log('titleHeight (總高度像素，已排除 <br>)', titleHeight);
      // console.log('lineHeight (行高像素)', lineHeight);
      // console.log('titleLines (總行數)', titleLines);
      // console.log('計算公式: Math.ceil(titleHeight / lineHeight) = Math.ceil(' + titleHeight + ' / ' + lineHeight + ') = ' + titleLines);
      // console.log('第一頁可用行數應該是: pageSize - titleLines = ' + pageSize + ' - ' + titleLines + ' = ' + (pageSize - titleLines));
    } finally {
      // 清理臨時容器
      document.body.removeChild(tempMeasureContainer);
    }
  } else {
    // console.log('=== .title 元素測量 ===');
    // console.log('未找到 .title 元素');
  }
  // ===== .title 測量結束 =====

  // ===== 統一處理：檢查 <div.img> 是否在第一個 .text 之前 =====
  // 這個邏輯對 topic 和 plaintext 都適用
  let shouldAddImgDivOffset = false;
  if (textElements.length > 0 && firstImgDiv) {
    const firstTextElement = textElements[0];
    const isImgDivPreceding = !!(firstImgDiv.compareDocumentPosition(firstTextElement) & Node.DOCUMENT_POSITION_FOLLOWING);
    
    if (isImgDivPreceding) {
      // 檢查 <div.img> 內是否有圖片
      const imagesInImgDiv = firstImgDiv.querySelectorAll('img');
      if (imagesInImgDiv.length > 0) {
        shouldAddImgDivOffset = true; // 需要將第一頁行數減 1
      }
    }
  }
  // ===== 統一處理結束 =====

  // 創建通用的包裝函數，處理 <div.img> 的情況
  const createCountPrecedingLineElementsWrapper = () => {
    return (textElement) => {
      let count = countPrecedingLineElements(textElement);
      
      // 如果是第一個 .text 元素且 <div.img> 在前，需要減 1 行
      const isFirstTextElement = textElement === textElements[0];
      if (isFirstTextElement && shouldAddImgDivOffset) {
        count += 1;
      }
      
      return count;
    };
  };

  // 檢查 doc 並決定使用哪種分頁邏輯
  const hasTopic = extendedResourceStyle === LayoutType.Topic_108_2;
  const hasPlainText = extendedResourceStyle === LayoutType.PlainText;
  const hasImage = extendedResourceStyle === LayoutType.Image_108_2;

  if (hasTopic) {
    // === 新增：偵測多區塊結構 ===
    const questionEl = doc.querySelector('#question');
    const answerEl = doc.querySelector('#answer');
    const analyzeEl = doc.querySelector('#analyze');
    const hasMultiSection = !!(questionEl && (answerEl || analyzeEl));
    
    // ★ 多區塊結構時，titleHeight 只計算 #question 的 .title
    //   （原始 titleHeight 包含 #answer/#analyze 的 .title，會導致第一頁空間被過度壓縮）
    let effectiveTitleHeight = titleHeight;
    let effectiveTitleLines = titleLines;
    if (hasMultiSection) {
      effectiveTitleHeight = 0;
      const questionTitles = questionEl.querySelectorAll('.title');
      questionTitles.forEach(t => {
        measureContainer.innerHTML = `<div class="title">${t.innerHTML}</div>`;
        void measureContainer.offsetHeight;
        effectiveTitleHeight += measureContainer.offsetHeight;
      });
      effectiveTitleLines = effectiveTitleHeight > 0
        ? Math.ceil(effectiveTitleHeight / lineHeight)
        : 0;
    }
    
    // 多區塊時只處理 #question 內的 .text，否則處理全部
    const targetTextElements = hasMultiSection
      ? Array.from(textElements).filter(el => questionEl.contains(el))
      : Array.from(textElements);

    // 原有邏輯：每個 .text div 獨立分頁（改用 targetTextElements）
    targetTextElements.forEach((textElement) => {
      let precedingImgCount = countPrecedingLineElements(textElement);
      
      const isFirstTextElement = textElement === targetTextElements[0]; // ← 改用 targetTextElements
      if (isFirstTextElement && shouldAddImgDivOffset) {
        precedingImgCount += 1;
      }
      
      const nodes = collectTextNodes(textElement);
      
      const pages = splitNodesIntoPages(
        nodes, 
        precedingImgCount, 
        pageSize, 
        measureContainer, 
        lineHeight,
        isFirstTextElement ? effectiveTitleHeight : 0,
        isFirstTextElement ? effectiveTitleLines : 0
      );
      
      if (pages.length > 1) {
        replaceTextElementWithPages(textElement, pages);
      }
    });

    // === 新增：多區塊結構的頁面分配 ===

  if (hasMultiSection) {
    const questionTexts = questionEl.querySelectorAll('.text');
    const questionTextCount = questionTexts.length;
    const lastQText = questionTexts[questionTextCount - 1];
    
    const maxPageHeight = pageSize * lineHeight;
    let currentPageIdx = Math.max(questionTextCount - 1, 0);
    // 題幹最後一頁的索引。currentPageIdx 之後會被 options／answer 遞增，先存起來。
    const questionTextPageIdx = currentPageIdx;
    
    const outerWrapper = questionEl.parentElement;
    const hasOuterWrapper = outerWrapper && outerWrapper !== doc.body && outerWrapper !== doc.documentElement;
    if (hasOuterWrapper && outerWrapper.className) {
      measureContainer.className = outerWrapper.className;
    }
    
    // ★ 累積測量法：用當前頁的「全部內容」一起測量，避免獨立測量不準
    let cumulativeHTML = '';
    
    // 第一頁需包含 title 和前置圖片
    if (questionTextCount <= 1) {
      const qTitle = questionEl.querySelector('.title');
      if (qTitle) cumulativeHTML += qTitle.outerHTML;
      if (shouldAddImgDivOffset) {
        cumulativeHTML += `<div style="height:${lineHeight}px"></div>`;
      }
    }
    
    // 加入最後一個 .text 的內容
    if (lastQText) {
      cumulativeHTML += `<div class="text">${lastQText.innerHTML}</div>`;
    }
    
    // --- 收集 #question 內的非 .text 區塊 ---
    const optionBlocksSet = [];
    const optionBox = questionEl.querySelector('.option_box');
    const sideBySide = questionEl.querySelector('.side_by_side');
    const yearEl = questionEl.querySelector('.year');
    
    if (optionBox) optionBlocksSet.push(optionBox);
    if (sideBySide && !optionBlocksSet.includes(sideBySide)) optionBlocksSet.push(sideBySide);
    const optionBlocks = Array.from(optionBlocksSet);
    if (yearEl && !optionBlocks.some(b => b.contains(yearEl))) {
      optionBlocks.push(yearEl);
    }

    // --- #question 內「帶圖片但沒有任何分頁控制」的區塊，綁到題幹所在頁 ---
    //
    // display.js 的多區塊分支只逐頁控制四種東西：#question 的 .text（依索引）、
    // .title（只第 0 頁）、div.img（只第 0 頁）、帶 data-page-index 的元素。
    // 像 <div class="subject"><img></div> 這種包著題目附圖的 wrapper 不在其中，
    // 而 #question 本身從不隱藏，於是那張圖會在每一頁都被畫出來（同一個 DOM
    // 節點，不是複製）——外觀上就是「圖片重複 N 次」。
    //
    // NBK-1662 當初刻意不把這種圖抽成獨立頁，因為那會讓題幹頁反而看不到圖。
    // 這裡改成真正的逐頁控制：綁在題幹所在頁，其他頁隱藏，兩個問題都不會發生。
    //
    // 用結構判斷而非 class 名稱：.subject 在 topic108-2.css 裡根本沒有規則，
    // 靠名字列舉遲早漏掉別的內容變體。
    const questionImageBlocks = Array.from(questionEl.children).filter((child) => {
      if (!child.querySelector || !child.querySelector('img')) return false; // 沒有圖片就不需要處理
      if (child.querySelector('.text')) return false;      // 含題幹文字：由 .text 逐頁控制，不能整塊隱藏
      if (child.classList.contains('img') || child.classList.contains('imgSize')) return false; // 已由「只第 0 頁」控制
      if (child.classList.contains('text') || child.classList.contains('title')) return false;
      if (optionBlocks.some((b) => b === child || child.contains(b))) return false; // 已由 option 分頁控制
      if (child.hasAttribute('data-page-index')) return false;
      return true;
    });
    questionImageBlocks.forEach((block) => {
      block.setAttribute('data-page-index', String(questionTextPageIdx));
    });

    // C：把這些附圖區塊納入累積量測。
    // 原本 cumulativeHTML 只有 .title、前置圖佔位行、題幹 .text，然後才接
    // options / answer —— 等於「決定每頁塞什麼」時忽略了頁面上最大的元素
    // （本案的 .subject 數線圖實測 203px ≈ 2.3 行）。量測值遠小於實際渲染高度，
    // 再靠 adjustContentScale 事後補救。
    //
    // 注意：這會讓量測變大，因此在「題幹＋選項原本剛好塞得下」的內容上可能
    // 多切一頁。那是誠實反映實際溢出，但確實會增加頁數；根治要靠切頁前先評估
    // 縮放空間（見上一個 commit 的說明），屬另案。
    if (currentPageIdx === questionTextPageIdx) {
      questionImageBlocks.forEach((block) => {
        cumulativeHTML += block.outerHTML;
      });
    }
    
    // 嘗試放入 option 區塊（累積測量）
    if (optionBlocks.length > 0) {
      let testHTML = cumulativeHTML;
      optionBlocks.forEach(b => { testHTML += b.outerHTML; });
      measureContainer.innerHTML = `<div id="question">${testHTML}</div>`;
      void measureContainer.offsetHeight;
      const totalWithOptions = measureContainer.offsetHeight;
      
      if (totalWithOptions <= maxPageHeight) {
        optionBlocks.forEach(b => b.setAttribute('data-page-index', String(currentPageIdx)));
        cumulativeHTML = testHTML;
      } else {
        currentPageIdx++;
        cumulativeHTML = '';
        optionBlocks.forEach(b => { cumulativeHTML += b.outerHTML; });
        optionBlocks.forEach(b => b.setAttribute('data-page-index', String(currentPageIdx)));
      }
    }
    
    // 嘗試放入 #answer（整個區塊，保持結構完整）
    if (answerEl) {
      let testHTML = cumulativeHTML + answerEl.outerHTML;
      measureContainer.innerHTML = testHTML;
      void measureContainer.offsetHeight;
      
      if (measureContainer.offsetHeight <= maxPageHeight) {
        answerEl.setAttribute('data-page-index', String(currentPageIdx));
        cumulativeHTML = testHTML;
      } else {
        currentPageIdx++;
        cumulativeHTML = answerEl.outerHTML;
        answerEl.setAttribute('data-page-index', String(currentPageIdx));
      }
    }
    
    // 嘗試放入 #analyze（整個區塊，保持結構完整）
    if (analyzeEl) {
      let testHTML = cumulativeHTML + analyzeEl.outerHTML;
      measureContainer.innerHTML = testHTML;
      void measureContainer.offsetHeight;
      
      if (measureContainer.offsetHeight <= maxPageHeight) {
        analyzeEl.setAttribute('data-page-index', String(currentPageIdx));
      } else {
        currentPageIdx++;
        analyzeEl.setAttribute('data-page-index', String(currentPageIdx));
      }
    }
    
    return {
      htmlString: doc.body.innerHTML,
      totalPages: currentPageIdx + 1,
    };
  }
  
  } else if (hasPlainText) {
    // 如果有 plain_text class，使用新的邏輯：跨 .text div 分頁
    // 這樣可以讓後續 .text div 的內容填充到前一個 .text div 的不滿頁面中
    
    // 創建包裝函數，處理 <div.img> 和 PlainText 類型的情況
    const wrappedCountPrecedingLineElements = (textElement) => {
      let count = countPrecedingLineElements(textElement);
      
      // 統一處理：如果 <div.img> 在第一個 .text 之前，需要減 1 行
      const isFirstTextElement = textElement === textElements[0];
      if (isFirstTextElement && shouldAddImgDivOffset) {
        count += 1;
      }
      
      return count;
    };
    
    const pages = splitTextElementsAcrossPages(
      textElements,
      wrappedCountPrecedingLineElements,  // 使用包裝函數
      collectTextNodes,
      pageSize,
      measureContainer,
      lineHeight,
      titleHeight,  // 傳入 title 實際高度（像素）
      titleLines   // 傳入 title 行數
    );
    
    // 如果原本有多個 .text div，或者分頁後有多頁，都需要替換
    // 這樣可以確保多個 .text div 的內容能夠正確合併
    if (textElements.length > 1 || pages.length > 1) {
      replaceAllTextElementsWithPages(textElements, pages);
    }
  } else if (hasImage) {
    const originalWidth = measureContainer.style.width;
    const fullWidth = parseFloat(originalWidth);
    // .imageSet .abreast > div{width:50%}：右欄文字只有容器的一半寬，
    // 每一頁的 .text 都是 .abreast 的 flex 子元素，所以每頁都要用一半寬度量測。
    const halfWidth = fullWidth > 0 ? fullWidth * 0.5 : 0;

    // .title 算 1 行（固定扣除）
    const titleLines = titleElements.length > 0 ? 1 : 0;

    const allNodes = [];
    textElements.forEach((textElement) => {
      const nodes = collectTextNodes(textElement);
      allNodes.push(...nodes);
    });

    const pages = [];
    let currentPageHTML = '';

    // 每頁可用高度 = 容器高度 - 標題列（標題每頁都在，見 display.js 的 isImageSet 分支）。
    // 不用 pageSize × lineHeight 換算行數：lineHeight 取自最外層容器（預設 16px 字），
    // 跟圖片型右欄實際的字級與高度無關，換算出來的頁高只有實際欄高的兩成左右，
    // 會切出一堆下方大片留白的頁（NBK-1667）。
    const pageMaxHeight = measureImagePageHeight({
      measureContainer,
      titleElements,
      fullWidthCss: originalWidth,
      fallbackHeight: Math.max(pageSize - titleLines, 1) * lineHeight,
    });

    if (halfWidth > 0) {
      measureContainer.style.width = halfWidth + 'px';
    }

    allNodes.forEach((node) => {
      const nodeContent = node.content;
      if (!nodeContent) return;

      const effectiveMaxHeight = pageMaxHeight;

      const testHTML = `<div class="text">${currentPageHTML + nodeContent}</div>`;
      if (measureContainer.setHTMLAndApplyBrStyles) {
        measureContainer.setHTMLAndApplyBrStyles(testHTML);
      } else {
        measureContainer.innerHTML = testHTML;
      }

      const testHeight = measureContainer.offsetHeight;

      if (testHeight > effectiveMaxHeight + 1 && currentPageHTML) {
        pages.push(currentPageHTML);
        currentPageHTML = nodeContent;
      } else {
        currentPageHTML += nodeContent;
      }
    });

    if (currentPageHTML) {
      pages.push(currentPageHTML);
    }

    measureContainer.style.width = originalWidth;

    // --- 關鍵：每一頁的 .text 都與第一頁同層，才能維持「左圖右文各半」的版面 ---
    // 圖片型版面為 .imageSet .abreast{display:flex} + .abreast > div{width:50%}（左圖右文）
    if (pages.length > 1) {
      const firstTextElement = textElements[0];
      const textParent = firstTextElement.parentNode; // 與第一頁 .text 同層的容器（.abreast）

      firstTextElement.innerHTML = pages[0];

      for (let i = 1; i < textElements.length; i++) {
        if (textElements[i].parentNode) {
          textElements[i].parentNode.removeChild(textElements[i]);
        }
      }

      let insertAfter = firstTextElement;
      for (let i = 1; i < pages.length; i++) {
        const newTextDiv = doc.createElement('div');
        newTextDiv.className = 'text';
        newTextDiv.innerHTML = pages[i];
        textParent.insertBefore(newTextDiv, insertAfter.nextSibling);
        insertAfter = newTextDiv;
      }
    }
  } else {
    // 預設使用新的邏輯（跨 .text div 分頁）
    // 使用通用的包裝函數
    const wrappedCountPrecedingLineElements = createCountPrecedingLineElementsWrapper();
    
    const pages = splitTextElementsAcrossPages(
      textElements,
      wrappedCountPrecedingLineElements,
      collectTextNodes,
      pageSize,
      measureContainer,
      lineHeight,
      titleHeight,  // 傳入 title 實際高度（像素）
      titleLines   // 傳入 title 行數
    );
    
    if (textElements.length > 1 || pages.length > 1) {
      replaceAllTextElementsWithPages(textElements, pages);
    }
  }

  // 計算總頁數：統計所有 .text 元素（包括分頁後新增的）
  const allTextElements = doc.querySelectorAll('.text');
  actualTotalPages = allTextElements.length;

  // 為非標題圖片創建獨立分頁
  const standaloneImages = doc.querySelectorAll('img[data-standalone-page="true"]');
  if (standaloneImages.length > 0) {
    standaloneImages.forEach((img) => {
      img.style.display = 'block'; // 恢復原狀
    });
    const lastTextElement = allTextElements[allTextElements.length - 1];
    const lastTextParent = lastTextElement.parentNode;
    
    standaloneImages.forEach((img, index) => {
      // 為每個圖片創建一個獨立的 .text 分頁
      const imageTextDiv = doc.createElement('div');
      imageTextDiv.className = 'text';
      
      // 依畫面分類設置頁面索引屬性，用於後續顯示控制
      if (extendedResourceStyle === 'PlainText') {
        img.setAttribute('data-page-index', String(actualTotalPages + index));
      } else {
        img.setAttribute('data-page-index', String(actualTotalPages + index));
      }
      // 在最後一個 .text 之後插入新的分頁
      if (lastTextElement.nextSibling) {
        lastTextParent.insertBefore(imageTextDiv, lastTextElement.nextSibling);
      } else {
        lastTextParent.appendChild(imageTextDiv);
      }
    });
    
    // 更新總頁數
    actualTotalPages += standaloneImages.length;
  }

  // 處理內容圖片：找到在第一個 .text 之後，但不在任何 .text 內部的圖片
  if (allTextElements.length > 0) {
    const firstTextElement = allTextElements[0];
    const lastTextElementAtThisPoint = allTextElements[allTextElements.length - 1];
    
    // 找到內容圖片（不在第一個.text之前，不在最後一個.text之後，不在任何.text內部）
    const contentImages = [];
    const allImages = doc.querySelectorAll('img');
    
    allImages.forEach((img, index) => {
      // 跳過已經標記為獨立頁面的圖片
      if (img.getAttribute('data-standalone-page') === 'true') {
        return;
      }
      
      // 檢查圖片位置關係
      const position = img.compareDocumentPosition(firstTextElement);
      const isPreceding = !!(position & Node.DOCUMENT_POSITION_FOLLOWING);
      
      // 圖片不在第一個.text之前（不是標題圖片）
      const isNotPreceding = !isPreceding;
      
      // 圖片不在最後一個.text之後（不是最後一頁的圖片）
      // 但如果只有一個.text元素，且圖片在第一個.text之後，則視為內容圖片
      let isNotFollowing = true;
      if (lastTextElementAtThisPoint) {
        // 如果只有一個.text元素，且圖片在第一個.text之後，則視為內容圖片
        if (allTextElements.length === 1 && isNotPreceding) {
          isNotFollowing = true; // 只有一個.text時，圖片在第一個.text之後就是內容圖片
        } else {
          const followingPosition = lastTextElementAtThisPoint.compareDocumentPosition(img);
          const isFollowing = !!(followingPosition & Node.DOCUMENT_POSITION_FOLLOWING);
          isNotFollowing = !isFollowing;
        }
      }
      
      // 圖片不在任何.text元素內部
      const isNotInsideText = !Array.from(allTextElements).some(textEl => 
        textEl.contains(img)
      );
      
      if (isNotPreceding && isNotFollowing && isNotInsideText) {
        contentImages.push(img);
      }
    });
    
    // 如果有內容圖片，需要處理
    if (contentImages.length > 0) {
      // 計算前置的 <br> 和 <img> 數量（標題圖片每個算1行）
      const precedingBrCount = countPrecedingLineElements(firstTextElement);
      
      // 收集第一個 .text 內的所有節點
      const nodes = collectTextNodes(firstTextElement);
      
      // 按照行數限制切分成多頁（計算第一頁內容）
      const pages = splitNodesIntoPages(nodes, precedingBrCount, pageSize, measureContainer, lineHeight, titleHeight, titleLines);
      const firstPageContent = pages[0] || '';
      
      // 測量第一頁文字內容的高度
      const firstPageHTML = `<div class="text">${firstPageContent}</div>`;
      measureContainer.innerHTML = firstPageHTML;
      const firstPageHeight = measureContainer.offsetHeight;
      
      // 計算最大高度限制
      const maxHeight = pageSize * lineHeight;
      
      // 計算標題圖片占用的高度（標題圖片算1行）
      const titleImageHeight = lineHeight; // 標題圖片固定算1行
      
      // 判斷第一頁內容是否已滿
      const totalFirstPageHeight = firstPageHeight + titleImageHeight;
      
      // 使用容錯範圍：如果達到 90% 以上，就視為已滿
      const tolerance = Math.max(maxHeight * 0.1, 1); // 10% 或至少 1px
      const isFirstPageFull = totalFirstPageHeight >= (maxHeight - tolerance);
      
      // 如果第一頁已滿，且有內容圖片，必須創建第二頁
      if (isFirstPageFull && contentImages.length > 0) {
        // 將內容圖片標記為在第二頁顯示
        contentImages.forEach(img => {
          img.setAttribute('data-content-image', 'true');
          img.setAttribute('data-second-page', 'true');
        });
        
        // 如果只有一頁，創建.text作為第二頁來放置圖片
        if (actualTotalPages === 1) {
          const emptyTextDiv = doc.createElement('div');
          emptyTextDiv.className = 'text';
          
          // 在第一個.text之後插入空的.text
          const firstTextParent = firstTextElement.parentNode;
          const firstTextNextSibling = firstTextElement.nextSibling;
          if (firstTextNextSibling) {
            firstTextParent.insertBefore(emptyTextDiv, firstTextNextSibling);
          } else {
            firstTextParent.appendChild(emptyTextDiv);
          }
          
          actualTotalPages = 2;
        }
      }
    }
  }

  // 重新計算總頁數（因為可能新增了空的.text）
  const finalTextElements = doc.querySelectorAll('.text');
  actualTotalPages = finalTextElements.length;

  // 返回修改後的 HTML 字串和總頁數
  return {
    htmlString: doc.body.innerHTML,
    totalPages: actualTotalPages || 1,
  };
};
